import { Injectable, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { RestfulApiService } from '../restful-api.service';
import { CharityUserService, ToggleWatchlistPayload } from '../charity/charity-user.service';
import { FavoredDeal, FavoredDealResponse, FavoriteCardData } from '../../models/favorites';

@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  // State signals
  favoredCharities = signal<FavoriteCardData[]>([]);
  favoredProjects = signal<FavoriteCardData[]>([]);
  isLoading = signal<boolean>(false);
  error = signal<string | null>(null);

  constructor(
    private restfulApiService: RestfulApiService,
    private charityUserService: CharityUserService
  ) {}

  /**
   * Fetch favored charities from API
   * @param languageCode - Language code for the request
   */
  getFavoredCharities(languageCode: string): Observable<FavoriteCardData[]> {
    this.isLoading.set(true);
    this.error.set(null);

    return this.restfulApiService
      .get(`/v1/user-assets/favored-deals/ORGANISATION`, {
        languageCode,
      })
      .pipe(
        map((response) => {
          const typedResponse = response as FavoredDealResponse;
          const deals = typedResponse.response || [];
          return this.mapFavoredDealsToCards(deals, 'charity');
        }),
        tap((cards) => {
          this.favoredCharities.set(cards);
          this.isLoading.set(false);
        }),
        catchError((error) => {
          console.error('Error fetching favored charities:', error);
          this.error.set('Failed to load favored charities');
          this.isLoading.set(false);
          this.favoredCharities.set([]);
          return of([]);
        })
      );
  }

  /**
   * Fetch favored projects from API
   * @param languageCode - Language code for the request
   */
  getFavoredProjects(languageCode: string): Observable<FavoriteCardData[]> {
    this.isLoading.set(true);
    this.error.set(null);

    return this.restfulApiService
      .get(`/v1/user-assets/favored-deals/PROJECT`, {
        languageCode,
      })
      .pipe(
        map((response) => {
          const typedResponse = response as FavoredDealResponse;
          const deals = typedResponse.response || [];
          return this.mapFavoredDealsToCards(deals, 'project');
        }),
        tap((cards) => {
          this.favoredProjects.set(cards);
          this.isLoading.set(false);
        }),
        catchError((error) => {
          console.error('Error fetching favored projects:', error);
          this.error.set('Failed to load favored projects');
          this.isLoading.set(false);
          this.favoredProjects.set([]);
          return of([]);
        })
      );
  }

  /**
   * Remove an entity from favorites
   * @param entityUUID - UUID of the charity or project
   * @param fundType - Type of entity (ORGANISATION or PROJECT)
   */
  removeFromFavorites(
    entityUUID: string,
    fundType: 'ORGANISATION' | 'PROJECT'
  ): Observable<void> {
    const payload: ToggleWatchlistPayload = {
      entityUUID,
      watchlistType: 'REMOVE',
      fundType,
    };

    return this.charityUserService.toggleWatchlist(payload).pipe(
      map(() => {
        // Optimistically update local state
        if (fundType === 'ORGANISATION') {
          const currentCharities = this.favoredCharities();
          this.favoredCharities.set(
            currentCharities.filter((item) => item.uuid !== entityUUID)
          );
        } else {
          const currentProjects = this.favoredProjects();
          this.favoredProjects.set(
            currentProjects.filter((item) => item.uuid !== entityUUID)
          );
        }
        return;
      }),
      catchError((error) => {
        console.error('Error removing from favorites:', error);
        this.error.set('Failed to remove from favorites');
        throw error;
      })
    );
  }

  /**
   * Map FavoredDeal objects to FavoriteCardData format
   * @param deals - Array of FavoredDeal objects from API
   * @param type - Entity type for context
   * @returns Mapped and sorted array of FavoriteCardData
   */
  private mapFavoredDealsToCards(
    deals: FavoredDeal[],
    type: 'charity' | 'project'
  ): FavoriteCardData[] {
    const mapped = deals.map((deal) => {
      // Map based on type (charities have different field names than projects)
      if (type === 'charity') {
        // Calculate donation percentage for charities
        let donationPercentage = 0;
        if (deal.currentAmount && deal.targetAmount && deal.targetAmount > 0) {
          donationPercentage = Math.round((deal.currentAmount / deal.targetAmount) * 100);
        }

        return {
          uuid: deal.organisationUuid || '',
          imageUrl: deal.coverImageUrl || 'https://fdi-files.s3.amazonaws.com/public/image-placeholder.png',
          causeName: deal.causeName || '',
          title: deal.organisationName || '',
          organizationName: deal.organisationName,
          executiveSummary: deal.executiveSummary || '',
          raisedAmount: deal.currentAmount,
          goalAmount: deal.targetAmount,
          donationPercentage,
          currency: deal.currency || '$',
          type: type,
          languageCode: deal.languageCode || 'eng',
          givingLink: deal.givingLink,
        };
      } else {
        // Projects - use amountRaisedToDate and totalProjectBudget
        let donationPercentage = 0;
        if (deal.amountRaisedToDate && deal.totalProjectBudget && deal.totalProjectBudget > 0) {
          donationPercentage = Math.round((deal.amountRaisedToDate / deal.totalProjectBudget) * 100);
        }

        return {
          uuid: deal.projectUuid || deal.dealUUID || '',
          imageUrl: deal.coverImageUrl || 'https://fdi-files.s3.amazonaws.com/public/image-placeholder.png',
          causeName: deal.projectCauseName || deal.cause || '',
          title: deal.projectName || deal.dealName || '',
          organizationName: deal.organisationName,
          executiveSummary: deal.description || '',
          raisedAmount: deal.amountRaisedToDate,
          goalAmount: deal.totalProjectBudget,
          donationPercentage,
          currency: deal.amountRaisedToDateCurrencySymbol || deal.currency || '$',
          type: type,
          languageCode: deal.languageCode || 'eng',
          givingLink: deal.givingLink,
        };
      }
    });

    // Sort alphabetically by title
    return mapped.sort((a, b) => a.title.localeCompare(b.title));
  }

  /**
   * Clear all favorites state
   */
  clearState(): void {
    this.favoredCharities.set([]);
    this.favoredProjects.set([]);
    this.error.set(null);
    this.isLoading.set(false);
  }
}
